const HERO_IMAGE =
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&w=1600&q=80';

/**
 * Featured guide: youth soccer planning ages 13+ (U14–U19) for college and pro pathways,
 * with Columbus, Ohio club options, high-school coordination, and 2025–2026 fees.
 */
export const COLUMBUS_YOUTH_SOCCER_PLANNING_13_PLUS_POST = {
  id: 'featured-columbus-youth-soccer-planning-13-plus',
  slug: 'columbus-youth-soccer-planning-ages-13-plus-college-pro-pathway',
  title:
    'Youth Soccer Planning Ages 13+: Columbus Clubs, Fees & the College-to-Pro Pathway',
  excerpt:
    'A step-by-step guide for teens (U14–U19) pursuing college soccer or a pro contract in Columbus, Ohio—high school vs. club balance, Crew Academy, MLS NEXT, ECNL fees, recruiting timelines, and 2025–2026 cost tables.',
  publishedAt: '2026-08-16T16:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Family Soccer',
  blogCategory: 'club-news',
  audience: 'Ages 13+ · Teens & parents',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More Columbus soccer guides',
  relatedPosts: [
    {
      slug: 'columbus-youth-soccer-planning-kids-3-13-professional-pathway',
      title: 'Youth Soccer Planning for Kids Ages 3–13',
      blurb: 'the foundation roadmap before the teen years.',
    },
    {
      slug: 'soccer-team-game-day-checklist',
      title: 'Soccer Game Day Checklist',
      blurb: 'tournament and showcase packing list.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Ohio Open Soccer Fields Guide',
      blurb: 'extra training spots between club sessions.',
    },
  ],
  referenceLinks: [
    {
      label: 'Columbus Crew Academy — recruitment',
      url: 'https://www.columbuscrew.com/academy/recruitment',
    },
    {
      label: 'Columbus Crew Academy — Pre-Formation (U11–U14)',
      url: 'https://www.columbuscrew.com/academy/teams/pre-formation',
    },
    {
      label: 'Club Ohio National — MLS NEXT',
      url: 'https://www.clubohiosoccer.com/club-locations/national-regional',
    },
    {
      label: 'Ohio Elite — payment policy (PDF)',
      url: 'https://ohioelite.com/wp-content/uploads/2026/03/2025-2026-OHIO-ELITE-SOCCER-ACADEMY-PAYMENT-POLICY-AND-PROCEDURE-U6-U19-2020-2007-REGISTRATION-FROM-MAY-2025-JULY-2026.pdf',
    },
    {
      label: 'Ohio Premier — tryouts & fee schedule',
      url: 'https://opsoccer.com/tryouts/',
    },
    {
      label: 'MLS NEXT — elite boys pathway',
      url: 'https://www.mlsnext.com/',
    },
    {
      label: 'NCAA Eligibility Center',
      url: 'https://web3.ncaa.org/ecwr3/',
    },
    {
      label: 'Girls Academy league',
      url: 'https://girlssoccer.com/',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: 'Ages 13–19', label: 'Teen pathway window' },
        { value: '$750–$3,500+', label: 'Annual club fee range' },
        { value: 'Free', label: 'Crew Academy U15+' },
        { value: 'ECNL / MLS NEXT', label: 'Top showcases' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this guide',
      paragraphs: [
        'This is the companion to our **ages 3–13 Columbus soccer planning guide**. Once your player hits the teen years, the goals shift: **high school identity**, **college recruiting**, and—for a small fraction—**professional contracts** through MLS, NWSL, or USL pathways.',
        'In Columbus, teens typically juggle **Ohio high school soccer** (mostly fall season) with **year-round club soccer** at ECNL, Girls Academy, or MLS NEXT levels. The calendar gets crowded fast—this guide maps what to prioritize at each age and what local programs cost in **2025–2026**.',
        'Fees below are published club-year pricing as of August 2026. Budget **$2,000–$6,000+ beyond club fees** for travel, hotels, showcases, and gear at elite teen levels.',
      ],
      chips: [
        'Ages 13+',
        'Columbus OH',
        'College recruiting',
        'MLS NEXT',
        'ECNL',
        'High school soccer',
      ],
      callout: {
        text: 'Read the ages 3–13 foundation guide first →',
        url: '/blog/columbus-youth-soccer-planning-kids-3-13-professional-pathway',
      },
    },
    {
      type: 'panel',
      title: 'The teen pathway — college, pro, and everything between',
      list: [
        '**Club soccer (year-round):** Primary platform for college coaches and pro scouts. ECNL, Girls Academy, and MLS NEXT are the top visibility leagues.',
        '**High school soccer (fall):** Community, school pride, and additional games—but college coaches rarely build recruiting lists from Friday-night high school matches alone.',
        '**Columbus Crew Academy (U15+):** Fully funded, **free-to-play** MLS pathway for invited players. U11–U14 Pre-Formation trains twice weekly while staying at home clubs.',
        '**Club Ohio MLS NEXT Homegrown:** Fee-based club route to MLS NEXT national competition—the only Central Ohio club promoted to MLS NEXT Homegrown tier (2026).',
        '**College (NCAA / NAIA):** Most elite teen players target D1, D2, D3, or NAIA programs. Register with the **NCAA Eligibility Center** by sophomore year.',
        '**Professional:** MLS/NWSL first teams, USL Championship, or overseas contracts—typically after academy, college, or direct scouting from MLS NEXT/ECNL.',
      ],
    },
    {
      type: 'dayBlocks',
      title: 'Step-by-step planning by age (13–19)',
      intro:
        'Soccer age groups use birth-year cutoffs (e.g., born 2011 = U15 in 2025–26). High school grade often aligns but not always—confirm with your club registrar.',
      days: [
        {
          title: 'Age 13 (U14) — First teen year',
          meals: [
            'Focus: Full 11v11 tactical roles; aerobic fitness; mental composure under pressure.',
            'Club: ECNL, ECNL RL, MLS NEXT Academy, or strong regional competitive team.',
            'High school: May play varsity or JV depending on district; coordinate with club coach on load.',
            'Recruiting: Too early for most D1 contact rules, but attend ID camps if invited. Build highlight video foundation.',
            'Columbus options: Ohio Elite ECNL/RL U13–U14; Club Ohio MLS NEXT AD/HG; Ohio Premier ECNL; Sporting Columbus; Columbus United.',
            'Fees: Ohio Elite ECNL **$3,300/year**; ECNL RL **$2,900/year**; Club Ohio elite **~$2,395–$2,695**.',
          ],
        },
        {
          title: 'Age 14 (U15) — Academy gateway',
          meals: [
            'Focus: Speed of play, positional specialization, leadership on the field.',
            'Club: Top-tier league required for serious college track—ECNL, GA, or MLS NEXT.',
            'Crew Academy: U15 is entry to **full-time, free** Crew Academy for invited players. Pre-Formation (U13–U14) feeds this pipeline.',
            'High school: Fall varsity likely; discuss spring club commitment with both coaches.',
            'Recruiting: D1 coaches can begin sending general materials; attend 2–3 quality showcases, not every weekend.',
            'Fees: Ohio Elite U15–U17 ECNL **$3,300/year**; Ohio Premier ECNL **~$3,350/year**; Club Ohio MLS NEXT **~$2,695**.',
          ],
        },
        {
          title: 'Ages 15–16 (U16–U17) — Peak recruiting years',
          meals: [
            'Focus: Game intelligence, consistency, injury prevention (growth plates still maturing).',
            'Club: 3+ sessions/week common; national showcase events (ECNL Nationals, GA Champions, MLS NEXT Fest).',
            'High school: Strong fall season helps confidence; club spring season drives recruiting exposure.',
            'Recruiting: Register NCAA Eligibility Center; email target coaches; attend college ID camps; update highlight reel every season.',
            'ODP / national pools: State and regional ODP can supplement club exposure for some players.',
            'Fees: Ohio Elite ECNL **$3,300/year**; ECNL RL **$3,100/year**; payment plans with $3,370 deposit + 9 installments.',
          ],
        },
        {
          title: 'Ages 17–18 (U18–U19) — Signing season',
          meals: [
            'Focus: Physical peak preparation; pro habits (sleep, nutrition, recovery).',
            'Club: Senior ECNL/GA/MLS NEXT teams; some players join **fall-only** club teams after high school season (~$750 fall fee at Ohio Elite).',
            'Pro pathway: MLS NEXT U19, Crew Academy U18, or USL Academy opportunities for top prospects.',
            'College: Verbal commitments often happen junior/senior year; NLI signing periods in fall and spring.',
            'Alternative: Gap year, semi-pro (USL League Two), or overseas academy trials for late bloomers.',
            'Fees: Ohio Elite U19 ECNL **$2,900/year**; ECNL RL **$2,800/year**; fall HS bridge team **$750** (Ohio Elite).',
          ],
        },
        {
          title: 'Age 19+ — Beyond youth soccer',
          meals: [
            'College freshman soccer (if enrolled) or transfer portal for players changing schools.',
            'Pro: Columbus Crew 2 (MLS Next Pro), USL clubs, or international opportunities.',
            'Club alumni: Many Columbus ECNL/MLS NEXT players land at Ohio State, Akron, Wright State, Marquette, and other D1 programs.',
            'Stay connected: Club directors and Crew Academy staff often help with placement recommendations.',
          ],
        },
      ],
    },
    {
      type: 'panel',
      title: 'High school vs. club — the Columbus calendar',
      paragraphs: [
        '**Ohio high school soccer** runs primarily in the **fall** (August–October state tournament). Most Columbus-area teens play for their school team and their club simultaneously in fall—a demanding load. Club coaches and high school coaches should communicate about training volume to reduce injury risk.',
        '**Club soccer** continues through winter (indoor/futsal) and spring league play. Elite clubs like Ohio Elite include **8 weeks of winter indoor training** for U12–U14; teen teams often train indoors November–February before spring ECNL/GA fixtures.',
        '**Spring overlap:** Some clubs pause or lighten spring club load for high school players in districts where spring sports conflict. Ohio Elite offers **fall-only high school bridge teams** at **$750** for trapped 8th graders and fall HS players.',
        '**Rule of thumb:** If college soccer is the goal, **club league level matters more than high school wins**. If high school experience matters to your child, find a club that supports dual participation rather than forcing a choice.',
      ],
      orderedList: [
        'August–October: High school season + club training (manage total hours)',
        'November–December: Club tournaments; high school postseason for some',
        'January–May: Club spring league, ECNL conference play, showcases',
        'June–July: Optional camps, ODP, college ID camps, family rest block',
      ],
    },
    {
      type: 'table',
      title: 'Columbus-area programs for ages 13+',
      intro:
        'Teen players in Central Ohio most often train with these clubs or the Crew Academy. MLS NEXT and ECNL are the primary national visibility platforms.',
      headers: ['Program', 'Age groups', 'Pathway', 'Cost model', 'Best for'],
      rows: [
        [
          'Columbus Crew Academy',
          'U15, U16, U18 (full-time); U11–U14 Pre-Formation (hybrid)',
          'MLS first team, MLS NEXT Pro',
          '**Free** (fully funded) for invited full-time players',
          'Top boys identified for pro track; no open tryouts—invitation only',
        ],
        [
          'Club Ohio National',
          'U12–U19',
          'MLS NEXT Homegrown, MLS NEXT Academy, Girls Academy',
          'Fee-based (~$2,295–$2,695+)',
          'Boys MLS NEXT route without Crew invitation; GA girls nationally',
        ],
        [
          'Ohio Elite Soccer Academy',
          'U6–U19',
          'ECNL, ECNL RL, Pre-ECNL',
          'Fee-based ($2,800–$3,300/year teen tiers)',
          'Strongest girls ECNL pipeline in region; boys ECNL RL',
        ],
        [
          'Ohio Premier (OP Soccer)',
          'U13–U19',
          'ECNL, ECNL RL, NPL',
          'Fee-based (~$3,350/year ECNL)',
          '30-year Central Ohio club; scholarships available',
        ],
        [
          'Sporting Columbus',
          'U7–U19',
          'Regional competitive, some national events',
          'Fee-based (~$2,200+ base)',
          'Structured programming; bundled event packages',
        ],
        [
          'Columbus United',
          'Youth competitive',
          'Local/regional leagues',
          'Fee-based ($325 club fee)',
          'Value option for teens not pursuing national showcases',
        ],
        [
          'Columbus Rec Parks',
          '13–15 (Bantams rec)',
          '$50/season',
          'Recreational only—not a college recruiting platform',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Columbus Crew Academy — the free pro pathway',
      paragraphs: [
        'The **Columbus Crew Academy** is a **fully funded, free-to-play** professional academy overseen by Crew leadership. Full-time teams compete in **MLS NEXT** at **U15, U16, and U18** age groups—the highest boys youth league in the U.S.',
        '**Pre-Formation (U11–U14):** Players train twice weekly at Crew facilities but **stay with their home club** (e.g., Club Ohio) for league play. This hybrid model feeds the full-time U15 academy. ID sessions are **invitation only**, held three times per year for U11/U12.',
        '**Recruitment:** No open tryouts. Submit player info via the Crew Academy recruitment page; club directors can email **academyscout@columbuscrew.com**. Crew Academy won the **MLS NEXT U19 championship** in 2025–26.',
        '**Facilities:** Historic Crew Stadium, OhioHealth Performance Center, ScottsMiracle-Gro Field. Homestay and Crew School programs exist for out-of-market full-time players.',
      ],
      callout: {
        text: 'Crew Academy player recruitment →',
        url: 'https://www.columbuscrew.com/academy/recruitment',
      },
    },
    {
      type: 'table',
      title: '2025–2026 fee guide — teen elite tiers (Ohio Elite)',
      intro:
        'Ohio Elite publishes detailed annual fees for U15–U19. Uniforms and travel are **not included**. Deposits and 9-month payment plans available.',
      headers: ['Age group / track', 'Birth years (2025–26)', 'Annual fee', 'Deposit (plan)'],
      rows: [
        [
          'U13–U14 ECNL',
          '2013–2012',
          '$3,300/year',
          '$3,370',
        ],
        [
          'U13–U14 ECNL RL',
          '2013–2012',
          '$2,900/year',
          '$2,970',
        ],
        [
          'U15–U17 ECNL',
          '2011–2009',
          '$3,300/year',
          '$3,370',
        ],
        [
          'U15–U17 ECNL RL',
          '2011–2009',
          '$3,100/year',
          '$3,170',
        ],
        [
          'U19 ECNL',
          '2008–2007',
          '$2,900/year',
          '$2,970',
        ],
        [
          'U19 ECNL RL',
          '2008–2007',
          '$2,800/year',
          '$2,870',
        ],
        [
          'Fall HS / trapped 8th grader',
          '2011–2007',
          '$750 (fall only)',
          '$775',
        ],
      ],
    },
    {
      type: 'table',
      title: '2025–2026 fee guide — other Columbus clubs (teen reference)',
      intro:
        'Reference tiers from club publications and playclubsoccer.org. Confirm at registration—fees vary by team level and location.',
      headers: ['Club', 'Age / level', 'Annual base fee', 'Typical extras'],
      rows: [
        [
          'Ohio Premier ECNL / ECNL RL',
          'U13–U19',
          '~$3,350/year',
          'Financial assistance available; see official fee schedule',
        ],
        [
          'Club Ohio National / MLS NEXT',
          'U12–U19',
          '~$2,295–$2,695',
          'Uniform, team camp, personal travel',
        ],
        [
          'Sporting Columbus',
          'U11–U19',
          '~$2,200 base',
          '+$550 regional surcharge; uniforms & travel excluded; $50 facility fee',
        ],
        [
          'Columbus United',
          'Competitive youth',
          '$325/year',
          'Uniforms, travel, tournaments separate',
        ],
        [
          'Columbus Rec Parks Bantams',
          'Ages 13–15 rec',
          '$50/season',
          'Uniform included; 6-game fall season',
        ],
        [
          'Columbus Crew Academy',
          'U15–U18 full-time',
          '$0',
          'Invitation only; fully funded',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'College recruiting checklist for Columbus families',
      orderedList: [
        '**Sophomore year:** Register at the NCAA Eligibility Center (eligibilitycenter.org). Maintain GPA and core-course requirements.',
        '**Freshman–junior:** Build a 3–5 minute highlight video (game film, not just skills). Update after each season.',
        '**Junior year:** Email target college coaches with video, schedule, and academic info. Attend 2–4 showcases where those coaches scout (ECNL events, college ID camps).',
        '**Senior year:** Narrow list; campus visits; verbal offers; sign NLI during designated windows if committing.',
        '**Club support:** Ohio Elite, OP Soccer, and Club Ohio provide college profile tools (e.g., Total Global Sports, Instat) for ECNL/GA players—use them.',
        '**Ohio colleges to research:** Ohio State, Akron, Wright State, Ohio University, Xavier, Dayton, Cleveland State, and regional D2/D3 programs.',
      ],
      callout:
        'NCAA rules limit when coaches can contact players—your club director should brief families on D1, D2, and D3 recruiting calendars.',
    },
    {
      type: 'panel',
      title: 'Club spotlight — Club Ohio MLS NEXT (teen years)',
      paragraphs: [
        '**Club Ohio National** is the fee-based MLS NEXT pipeline in Central Ohio. In September 2026 the club was promoted to **MLS NEXT Homegrown**—one of five clubs advancing from the Academy division—alongside existing **Girls Academy** national teams.',
        'Teen boys teams are organized under **MLS NEXT Homegrown (HG)** and **MLS NEXT Academy (AD)** tiers from U13 through U19. Girls compete in **Girls Academy** at U15–U19 with training at **Kilbourne Run Sports Park** and the Dublin training center.',
        'Club Ohio bundles league fees, referee costs, tournament entries, and indoor training into club fees. Families still pay for **uniforms, team camp, and travel**. Open tryouts are typically **free**; registration opens around April 1.',
      ],
      callout: {
        text: 'Club Ohio National / MLS NEXT →',
        url: 'https://www.clubohiosoccer.com/club-locations/national-regional',
      },
    },
    {
      type: 'panel',
      title: 'Parent planning checklist — ages 13 to 19',
      orderedList: [
        '**Age 13:** Confirm club is in ECNL, GA, MLS NEXT, or strong regional league if college is a goal.',
        '**Age 14:** Evaluate Crew Academy Pre-Formation or MLS NEXT ID opportunities with club director—honest assessment, not parental push.',
        '**Ages 15–16:** NCAA Eligibility Center registration; curated showcase schedule; monitor training load vs. high school.',
        '**Ages 17–18:** Finalize college list or pursue pro trials; consider fall-only club option if burned out.',
        '**Every year:** One full week off from structured soccer; strength training 2x/week; sleep 8+ hours during school.',
        '**Budget:** Plan $5,000–$10,000 all-in at elite teen levels including flights, hotels, and multiple showcase weekends.',
      ],
    },
    {
      type: 'hubLinks',
      title: 'Related guides on Daily Sports Life',
      groups: [
        {
          title: 'Foundation & prep',
          links: [
            {
              slug: 'columbus-youth-soccer-planning-kids-3-13-professional-pathway',
              label: 'Youth soccer planning ages 3–13',
              meta: 'Columbus clubs & fees for younger kids',
            },
            {
              slug: 'soccer-team-game-day-checklist',
              label: 'Soccer game day checklist',
              meta: 'tournament packing list',
            },
            {
              slug: 'soccer-field-injury-remedies-calf-hamstring',
              label: 'Soccer field injury remedies',
              meta: 'calf and hamstring care',
            },
          ],
        },
        {
          title: 'Training drills',
          links: [
            {
              slug: 'intermediate-soccer-goalkeeper-practice-warm-up-wall-setup',
              label: 'Intermediate goalkeeper practice',
              meta: 'wall warm-up setup',
            },
            {
              slug: 'soccer-defender-drills-free-youtube-videos',
              label: 'Defender drills',
              meta: 'YouTube drill library',
            },
            {
              slug: 'soccer-forward-striker-drills-free-youtube-videos',
              label: 'Forward / striker drills',
              meta: 'finishing and movement',
            },
          ],
        },
      ],
    },
    {
      type: 'panel',
      title: 'Disclaimer',
      paragraphs: [
        'Fees, recruiting rules, and tryout dates change every season. Verify all pricing and policies on official club and NCAA websites before registering or budgeting.',
        'Daily Sports Life is not affiliated with any club, the Columbus Crew, or the NCAA. This article is informational—not legal, financial, or medical advice.',
      ],
    },
  ],
};
